import {
  ReportBatchTargetLabelModifier,
  ReportStandardFundReportTypeLabelModifier,
  ReportStandardFundsParamsModel,
} from '@/features/mold/models';
import { t } from '@/i18n';
import type { jsonType } from '@/utils';
import type { z } from 'zod';
import {
  BatchStatusLabelModifier,
  type batchResultStatusType,
  type batchResultType,
  batchTargetLabel,
  type batchType,
} from '../_models/batch';

export const convertBatchTarget = (
  _target: string | null,
  type: z.infer<typeof batchType>,
  batchParams: z.infer<typeof jsonType>,
) => {
  if (type === 'bulk-update-organization-excluded-flags') {
    return t('organization');
  }

  if (
    type === 'json-output-for-csv-download' ||
    type === 'json-output-for-pdf-download' ||
    type === 'csv-bulk-upload'
  ) {
    const target = batchTargetLabel.parse(_target);

    switch (target) {
      case 'organizations-list':
        return t('organization-list');
      case 'users_download_list': {
        return t('user-list');
      }
      case 'users_upload_list': {
        return t('upload-users-result');
      }
      case 'rs-project-list':
        return t('batch.project-list-rs');
      case 'ss-project-list':
        return t('batch.project-list-ss');
      case 'ks-project-list':
        return t('batch.project-list-ks');
      case 'ks-investment-list':
        return t('report-type-investment-fund');
      case 'ks-local-government-list-A':
        return t('batch.report-type-local-government-fund-A');
      case 'ks-local-government-list-B':
        return t('batch.report-type-local-government-fund-B');
      case 'ks-standard-list': {
        const { reportType } =
          ReportStandardFundsParamsModel.parse(batchParams);
        return t('batch.report-type-standard-fund', {
          target: ReportStandardFundReportTypeLabelModifier[reportType],
        });
      }
      case 'rs-report1':
      case 'rs-report2':
      case 'rs-report3':
      case 'rs-report4':
      case 'rs-report5': {
        return ReportBatchTargetLabelModifier[target];
      }
      case 'payment-group-upload': {
        return t('payment-group-upload');
      }
      case 'contractor-payment-upload': {
        return t('contractor-payment-upload');
      }
      default: {
        const check: never = target;
        throw new Error(`Unexpected target: ${check}`);
      }
    }
  }

  return _target;
};

export const convertBatchStatus = ({
  type,
  resultType,
  status,
  processedCount,
  targetCount,
}: {
  type: z.infer<typeof batchType>;
  resultType: z.infer<typeof batchResultType> | undefined | null;
  status: z.infer<typeof batchResultStatusType>;
  processedCount: number | null | undefined;
  targetCount: number | null | undefined;
}) => {
  switch (resultType) {
    case 'error_bad_request':
      return t('batch.result-error-with-batch-status', {
        batchStatus: BatchStatusLabelModifier[status],
      });
    default: {
      const label = BatchStatusLabelModifier[status];
      if (status === 'processing') {
        // batch処理中しか存在しないため処理完了時のことを考慮し表示しないようにする
        const isNull =
          typeof processedCount !== 'number' || typeof targetCount !== 'number';

        if (type !== 'bulk-print' || isNull) {
          return label;
        }

        const count = [processedCount, targetCount].join('/');
        return `${label} (${count})`;
      }
      return label;
    }
  }
};
